import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import style from './pdp-forecast-version-selector.module.scss';
import { parseMomentUtc, US_DATE_FORMAT } from 'utils/dates';
import { MultiSelectForm } from 'components/multi select form/multi-select-form';
import { Button, DefinitionTooltip } from '@carbon/react';
import {
  applyPdpForecastAtom,
  selectedDcaScenarioAtom,
  useProductionForecastsVersionsAtom,
} from '../atoms';
import { WellVersionsResponse } from 'types/api-responses';
import { DUMMY_DCA_SCENARIOS } from './dca-scenario-selector';
import { capitalize } from 'lodash-es';

interface PdpForecastVersionSelectorProps {
  uwi: string;
  legacyPdpForecastVersionsHref: string;
  insightsPdpForecastVersionsHref: string;
  onApply: (version: ForecastVersion, declineType?: 'PDP' | 'TC') => void;
  onSelectedVersionsChange?: (
    versions: ForecastVersion[],
    declineType?: 'PDP' | 'TC'
  ) => void;
  includeAllVersions?: boolean;
}

export type PdpForecastSource = 'Legacy' | 'Insights';

export interface ForecastVersion {
  id: string;
  name: string;
  label?: string;
  versionName?: string;
  beginTime: string;
  endTime?: string | null;
  versionDisplayTime?: string;
  versionId: string;
  /** ISO date string for "Created at" display */
  createdAt?: string;
  /** DCA Scenario name for metadata */
  dcaScenario?: string;
  source: PdpForecastSource;
}

const DISABLED_TOOLTIP_ZERO = 'Please select one PDP Forecast.';
const DISABLED_TOOLTIP_MULTIPLE =
  'Multiple PDP Forecasts are selected, please select one.';

function mapApiVersionToForecastVersion(
  version: WellVersionsResponse & { uwi?: string },
  source: PdpForecastSource
): ForecastVersion {
  const compositeId = `${source}-${version.versionId}`;
  return {
    id: compositeId,
    name: version.versionName,
    versionName: version.versionName,
    versionId: version.versionId,
    beginTime: version.beginTime,
    endTime: version.endTime ?? null,
    versionDisplayTime: version.versionDisplayTime,
    createdAt: version.beginTime,
    dcaScenario: undefined,
    source,
  };
}

export const PdpForecastVersionSelector = ({
  uwi,
  legacyPdpForecastVersionsHref,
  insightsPdpForecastVersionsHref,
  onApply,
  onSelectedVersionsChange,
  includeAllVersions = false,
}: PdpForecastVersionSelectorProps) => {
  const [selectedVersions, setSelectedVersions] = useState<ForecastVersion[]>(
    []
  );
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const applyPdpForecast = useAtomValue(applyPdpForecastAtom);

  const isDummyScenario = useMemo(
    () =>
      DUMMY_DCA_SCENARIOS.some((dummy) => dummy.id === selectedDcaScenario?.id),
    [selectedDcaScenario?.id]
  );

  const forecastSource: PdpForecastSource = isDummyScenario
    ? 'Legacy'
    : 'Insights';
  const forecastHref = isDummyScenario
    ? legacyPdpForecastVersionsHref
    : insightsPdpForecastVersionsHref;

  const declineType = useMemo(() => {
    if (!selectedDcaScenario?.id) return undefined;
    const dcaScenarioId = selectedDcaScenario?.id;
    if (String(dcaScenarioId) === 'dummy-1') {
      return 'PDP';
    } else if (String(dcaScenarioId) === 'dummy-2') {
      return 'TC';
    }
    return undefined;
  }, [selectedDcaScenario?.id]);

  useEffect(() => {
    onSelectedVersionsChange?.(selectedVersions, declineType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVersions, onSelectedVersionsChange]);

  useEffect(() => {
    setSelectedVersions([]);
  }, [selectedDcaScenario?.id]);

  // List only the selected scenario's Insights versions. The Legacy entries carry
  // string ids such as "dummy-1" and read the legacy endpoint, which has no scenarios.
  const scenarioId = selectedDcaScenario?.id;
  const insightsScenarioId =
    !isDummyScenario && typeof scenarioId === 'number' ? scenarioId : undefined;

  const { searchAtom: versionsAtom } = useProductionForecastsVersionsAtom(
    forecastHref,
    uwi,
    isDummyScenario,
    !isDummyScenario && includeAllVersions,
    insightsScenarioId
  );
  const [versionsState, triggerSearch] = useAtom(versionsAtom);

  useEffect(() => {
    triggerSearch(undefined);
  }, [forecastHref, triggerSearch]);

  const options = useMemo(() => {
    const results = versionsState?.data?.results ?? [];
    const versions = results.map((v) =>
      mapApiVersionToForecastVersion(
        v as WellVersionsResponse & { uwi?: string },
        forecastSource
      )
    );

    // Insights versions arrive in the server's Well::DcaForecast.newest_first order (promoted
    // first, unpromoted drafts last; the versions view keeps it), which is also the order
    // "Version N" is numbered in, so keep it. Legacy versions are sorted newest first by date.
    if (forecastSource === 'Insights') return versions;

    return versions.sort((a, b) => {
      const dateA = new Date(a.beginTime).getTime();
      const dateB = new Date(b.beginTime).getTime();
      return dateB - dateA;
    });
  }, [versionsState?.data?.results, forecastSource]);

  const isLoading = versionsState?.loading === true;
  const isApplyEnabled = selectedVersions.length === 1;

  return (
    <>
      <MultiSelectForm<ForecastVersion>
        id="pdp-forecast-version-selector"
        options={options}
        value={selectedVersions}
        onChange={setSelectedVersions}
        isLoading={isLoading}
        onSearch={(text, option) =>
          option.name.toLowerCase().includes(text.toLowerCase())
        }
        renderLabel={(version) => (
          <ForecastVersionLabel
            version={version}
            scenarioName={selectedDcaScenario?.name || '--'}
          />
        )}
      />
      <ApplyButton
        isDisabled={!isApplyEnabled || applyPdpForecast.loading}
        selectedCount={selectedVersions.length}
        onApply={() => {
          onApply(selectedVersions[0], declineType);
        }}
      />
    </>
  );
};

interface ForecastVersionLabelProps {
  version: ForecastVersion;
  scenarioName: string;
}

const ForecastVersionLabel = ({
  version,
  scenarioName,
}: ForecastVersionLabelProps) => {
  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    return parseMomentUtc(dateString).format(US_DATE_FORMAT);
  };

  const createdDate = formatDate(version.createdAt ?? version.beginTime);
  const source = version.source;

  const label = useMemo(() => {
    let name: string;
    switch (scenarioName) {
      case 'pdp default':
        name = 'PDP Default';
        break;
      case 'mlle':
        name = 'MLLE';
        break;
      default:
        name = capitalize(scenarioName);
    }

    return `${name}: ${createdDate} | ${source}`;
  }, [scenarioName, createdDate, source]);

  return (
    <div className={style.versionLabel}>
      <div className={style.versionName}>
        {version.name.replace(/^Version v(\d+)$/i, 'Version $1')}
      </div>
      <div className={style.versionDetails}>{label}</div>
    </div>
  );
};

const ApplyButton = ({
  isDisabled,
  selectedCount,
  onApply,
}: {
  isDisabled: boolean;
  selectedCount: number;
  onApply?: () => void;
}) => {
  const disabledTooltipText =
    selectedCount === 0 ? DISABLED_TOOLTIP_ZERO : DISABLED_TOOLTIP_MULTIPLE;

  if (isDisabled) {
    return (
      <div className={style.applyButtonContainer}>
        <DefinitionTooltip
          className={style.applyButtonTooltip}
          definition={disabledTooltipText}
          align="top-end"
        >
          <span className={style.applyButtonTooltipWrap}>
            <Button
              disabled
              className={style.applyButton}
              size="sm"
              kind="primary"
              onClick={() => {}}
            >
              Apply
            </Button>
          </span>
        </DefinitionTooltip>
      </div>
    );
  }
  return (
    <div className={style.applyButtonContainer}>
      <Button
        className={style.applyButton}
        size="sm"
        kind="primary"
        onClick={onApply}
      >
        Apply
      </Button>
    </div>
  );
};
